import { NuxtLink } from "#components";
import { useLocalePath } from "#i18n";
import {
  computed,
  defineComponent,
  type DefineComponent,
  type PropType,
  type SlotsType,
} from "vue";
import type { JSX } from "vue/jsx-runtime";
import cn from "~/utils/cn";
import { resolveLink } from "~/utils/links";
import type { LinkFragment } from "~~/graphql/fragments/link-fragment";

type Variant = "fill" | "outline";
type Tone = "purple" | "yellow" | "white" | "ink" | "orange";
type Size = "default" | "small";

// `small` matches the header type scale (text-small) so the Tickets button
// lines up with the nav items and language switch. Mobile default follows the
// Figma mobile pill spec: 18px padding, 14/16 label.
const SIZES: Record<Size, string> = {
  default: "p-18 text-small md:px-22 md:py-10 md:text-lead",
  small: "px-18 py-8 text-small",
};

// Every tone carries a 1.5px border in BOTH variants: `fill` needs it so the
// hover inversion (fill -> outline) doesn't change the button's box, and it
// also makes a fill and an outline button sitting side by side (hero, program
// rows) exactly the same height.
//
// `fill` hover = the tone's outline resting state ("invertieren on hover",
// per feedback). `orange` fills with `text-ink`, not white: white on #ff8e15
// is 2.30:1 and fails AA, ink is 5.82:1 (and matches the newsletter band).
const TONES: Record<Tone, { fill: string; outline: string }> = {
  ink: {
    fill: "border-[1.5px] border-ink bg-ink text-white hover:bg-transparent hover:text-ink",
    outline: "border-[1.5px] border-ink text-ink hover:bg-ink hover:text-white",
  },
  orange: {
    fill: "border-[1.5px] border-orange bg-orange text-ink hover:bg-transparent hover:text-orange",
    outline:
      "border-[1.5px] border-orange text-orange hover:bg-orange hover:text-ink",
  },
  purple: {
    fill: "border-[1.5px] border-purple bg-purple text-paper hover:bg-transparent hover:text-purple",
    outline:
      "border-[1.5px] border-purple text-purple hover:bg-purple hover:text-paper",
  },
  yellow: {
    fill: "border-[1.5px] border-secondary bg-secondary text-primary hover:bg-transparent hover:text-secondary",
    outline:
      "border-[1.5px] border-secondary text-secondary hover:bg-secondary hover:text-primary",
  },
  white: {
    fill: "border-[1.5px] border-white bg-white text-purple hover:bg-transparent hover:text-white",
    outline:
      "border-[1.5px] border-white text-white hover:bg-white hover:text-purple",
  },
};

/**
 * Festival button — pill shape, Lead type. Renders <button>, <a> (external)
 * or <NuxtLink> (internal) via the Dynamic-Tags pattern. Pass a CMS `link`
 * (LinkFragment) or a pre-localised `to`, else it's a plain <button>.
 */
export const Button = defineComponent({
  props: {
    variant: { type: String as PropType<Variant>, default: "fill" },
    tone: { type: String as PropType<Tone>, default: "purple" },
    size: { type: String as PropType<Size>, default: "default" },
    link: { type: Object as PropType<LinkFragment | null>, default: null },
    to: { type: String, default: "" },
    type: { type: String as PropType<"button" | "submit">, default: "button" },
    class: { type: String, default: "" },
  },
  slots: Object as SlotsType<{
    default: () => JSX.Element | string;
    icon?: () => JSX.Element;
  }>,
  setup(props, { slots }) {
    const localePath = useLocalePath();

    const resolved = computed(() => resolveLink(props.link));

    const tag = computed(() => {
      if (props.to || resolved.value) {
        return resolved.value?.external ? "a" : NuxtLink;
      }
      return "button";
    });

    const linkProps = computed(() => {
      if (props.to) return { to: localePath(props.to) };
      const r = resolved.value;
      if (!r) return { type: props.type };
      return r.external
        ? { href: r.href, target: r.target, rel: "noopener noreferrer" }
        : { to: localePath(r.href) };
    });

    return () => {
      const Tag = tag.value as string | DefineComponent;
      const cls = cn(
        "inline-flex items-center justify-center gap-10 rounded-[50px] leading-none whitespace-nowrap transition-colors [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]",
        SIZES[props.size],
        TONES[props.tone][props.variant],
        props.class,
      );
      return (
        <Tag {...linkProps.value} class={cls}>
          {slots.icon?.()}
          {slots.default?.()}
        </Tag>
      );
    };
  },
});
